import React, {useRef, useState, useEffect, useMemo, useCallback} from 'react';
import ProductCard from '@/modules/patient-dashboard/components/ProductCard';
import SectionContainer from '@/shared/components/SectionContainer';
import {Recommendation} from '@/shared/types/api';
import {ChevronDown, CircleCheck} from 'lucide-react';

type ProductCardItem = {
    key: string;
    name: string;
    category: string;
    description: string;
    price: string;
    image: string;
    discountPrice?: string;
    url?: string | null;
};

interface ProviderProductsProps {
    recommendations?: Recommendation[];
    loading?: boolean;
}

type ProductSortOption = 'default' | 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc' | 'category';

interface ProductFilterFields {
    query: string;
    category: string;
}

const ProviderProducts: React.FC<ProviderProductsProps> = ({recommendations = [], loading = false}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const [sortOption, setSortOption] = useState<ProductSortOption>('default');
    const [filterFields, setFilterFields] = useState<ProductFilterFields>({query: '', category: 'all'});
    const [appliedFilters, setAppliedFilters] = useState<ProductFilterFields>({query: '', category: 'all'});

    // Helper function to strip HTML tags and get plain text
    const stripHtml = (html: string): string => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    const categoryOptions = useMemo(() => {
        const categories = new Set<string>();
        recommendations.forEach((rec) => {
            const category = rec.sup_type?.trim() || 'Supplement';
            categories.add(category);
        });
        return Array.from(categories).sort((a, b) => a.localeCompare(b));
    }, [recommendations]);

    const handleFilterFieldChange = useCallback(
        (field: keyof ProductFilterFields) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            setFilterFields((prev) => ({...prev, [field]: event.target.value}));
        },
        []
    );

    const handleApplyFilters = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setAppliedFilters({
                query: filterFields.query,
                category: filterFields.category,
            });
            setIsFilterPanelOpen(false);
        },
        [filterFields.category, filterFields.query]
    );

    const handleResetFilters = useCallback(() => {
        const cleared = {query: '', category: 'all'} as const;
        setFilterFields(cleared);
        setAppliedFilters(cleared);
        setIsFilterPanelOpen(false);
    }, []);

    const hasActiveFilters = Boolean(appliedFilters.query.trim()) || appliedFilters.category !== 'all';

    const filteredRecommendations = useMemo(() => {
        const query = appliedFilters.query.trim().toLowerCase();
        const category = appliedFilters.category;

        return recommendations.filter((rec) => {
            if (!rec.enabled) return false;

            if (category !== 'all') {
                const recCategory = rec.sup_type?.trim() || 'Supplement';
                if (recCategory !== category) {
                    return false;
                }
            }

            if (query) {
                const name = rec.name?.toLowerCase() || '';
                if (!name.includes(query)) {
                    return false;
                }
            }

            return true;
        });
    }, [appliedFilters.category, appliedFilters.query, recommendations]);

    const sortedRecommendations = useMemo(() => {
        const output = [...filteredRecommendations];

        const getSortPrice = (rec: Recommendation) => {
            const value = typeof rec.price === 'number' ? rec.price : Number.POSITIVE_INFINITY;
            return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
        };

        switch (sortOption) {
            case 'name-asc':
                output.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                break;
            case 'name-desc':
                output.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
                break;
            case 'category':
                output.sort((a, b) =>
                    (a.sup_type?.trim() || 'Supplement').localeCompare(b.sup_type?.trim() || 'Supplement')
                );
                break;
            case 'price-asc':
                output.sort((a, b) => getSortPrice(a) - getSortPrice(b));
                break;
            case 'price-desc':
                output.sort((a, b) => getSortPrice(b) - getSortPrice(a));
                break;
            case 'default':
            default:
                break;
        }

        return output;
    }, [filteredRecommendations, sortOption]);

    // Convert API recommendations to product card format.
    const allProducts = useMemo<ProductCardItem[]>(() => {
        return sortedRecommendations.map((rec) => ({
            key: String(rec.id),
            name: rec.name,
            category: rec.sup_type || 'Supplement',
            description: rec.content ? stripHtml(rec.content) : 'Based on your lab results',
            price: rec.price ? `Starts at $${rec.price.toFixed(2)}` : 'Contact for pricing',
            discountPrice: rec.members_price ? rec.members_price.toFixed(2) : undefined,
            image: rec.image || '',
            url: rec.url,
        }));
    }, [sortedRecommendations]);

    const hasProducts = allProducts.length > 0;

    // Check if scrolling is needed
    useEffect(() => {
        const checkScrollNeeded = () => {
            if (scrollContainerRef.current) {
                const {scrollWidth, clientWidth} = scrollContainerRef.current;
                setShowScrollButtons(scrollWidth > clientWidth);
            }
        };

        checkScrollNeeded();
        window.addEventListener('resize', checkScrollNeeded);
        return () => window.removeEventListener('resize', checkScrollNeeded);
    }, [allProducts]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({left: 0});
        }
    }, [appliedFilters.category, appliedFilters.query, sortOption]);

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -283, // Scroll by width of one card plus gap (263px + 20px gap)
                behavior: 'smooth',
            });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: 283, // Scroll by width of one card plus gap (263px + 20px gap)
                behavior: 'smooth',
            });
        }
    };

    return (
        <SectionContainer
            title="🧬 BioMatch™ Products"
            headerExtra={
                <div className="flex flex-wrap gap-3 text-sm text-mld-muted-text">
                    <button className="hover:text-mld-primary" type="button">
                        Add New
                    </button>
                    <button
                        className="flex items-center gap-1 hover:text-mld-primary"
                        type="button"
                        aria-expanded={isFilterPanelOpen}
                        aria-controls="bioMatchProducts-filterPanel"
                        onClick={() => setIsFilterPanelOpen((prev) => !prev)}>
                        Filter by
                        <ChevronDown className="h-4 w-4 text-mld-muted-text" />
                    </button>
                    <div className="relative">
                        <button
                            className="flex items-center gap-1 hover:text-mld-primary"
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={isSortMenuOpen}
                            aria-controls="bioMatchProducts-sortMenu"
                            onClick={() => setIsSortMenuOpen((prev) => !prev)}>
                            Sort by
                            <ChevronDown className="h-4 w-4 text-mld-muted-text" />
                        </button>
                        {isSortMenuOpen && (
                            <div
                                id="bioMatchProducts-sortMenu"
                                role="menu"
                                aria-label="Sort products"
                                className="absolute right-0 z-10 mt-2 w-46 rounded-2xl border border-mld-panel-border bg-mld-background p-2 shadow-lg">
                                {[
                                    {label: 'Default', value: 'default'},
                                    {label: 'Product Name (A–Z)', value: 'name-asc'},
                                    {label: 'Product Name (Z–A)', value: 'name-desc'},
                                    {label: 'Category (A–Z)', value: 'category'},
                                    {label: 'Price (Low–High)', value: 'price-asc'},
                                    {label: 'Price (High–Low)', value: 'price-desc'},
                                ].map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={sortOption === option.value}
                                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm ${
                                            sortOption === option.value
                                                ? 'bg-mld-menu-background text-mld-primary'
                                                : 'text-mld-text-primary hover:bg-mld-menu-background'
                                        }`}
                                        onClick={() => {
                                            setSortOption(option.value as ProductSortOption);
                                            setIsSortMenuOpen(false);
                                        }}>
                                        {option.label}
                                        {sortOption === option.value && (
                                            <CircleCheck className="h-4 w-4 text-mld-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            }
            className="w-full max-w-full overflow-hidden min-w-0 !space-y-0"
            cardClassName="py-6 px-12"
            cardBackgroundClassName="bg-mld-menu-background">
            {isFilterPanelOpen && (
                <div
                    id="bioMatchProducts-filterPanel"
                    role="region"
                    aria-label="Product filters"
                    className="mb-4 rounded-2xl border border-mld-panel-border bg-mld-menu-background p-4">
                    <form onSubmit={handleApplyFilters} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="bioMatchProducts-query"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Product Name
                                </label>
                                <input
                                    id="bioMatchProducts-query"
                                    type="text"
                                    value={filterFields.query}
                                    onChange={handleFilterFieldChange('query')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="bioMatchProducts-category"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Category
                                </label>
                                <select
                                    id="bioMatchProducts-category"
                                    value={filterFields.category}
                                    onChange={handleFilterFieldChange('category')}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary">
                                    <option value="all">All</option>
                                    {categoryOptions.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    className="rounded-lg bg-mld-primary px-4 py-2 text-white shadow hover:bg-mld-primary/90">
                                    Apply Filters
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="rounded-lg border border-mld-panel-border px-4 py-2 text-mld-text-secondary hover:text-mld-primary">
                                    Reset
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            {hasActiveFilters && !loading && (
                <p className="mb-2 text-sm text-mld-muted-text">
                    Displaying {sortedRecommendations.length} filtered product(s).
                </p>
            )}

            {loading ? (
                <div className="text-center py-8 text-mld-card-text">Loading recommendations...</div>
            ) : !hasProducts ? (
                <div className="text-center py-8 text-mld-muted-text">
                    No BioMatch products have been configured for this organization.
                </div>
            ) : (
                <>
                    <div className="relative">
                        {/* Navigation arrows */}
                        {showScrollButtons && (
                            <>
                                <button
                                    type="button"
                                    aria-label="Scroll products left"
                                    onClick={scrollLeft}
                                    className="absolute -left-8 top-1/2 -translate-y-1/2 z-10 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 19l-7-7 7-7"
                                        />
                                    </svg>
                                </button>

                                <button
                                    type="button"
                                    aria-label="Scroll products right"
                                    onClick={scrollRight}
                                    className="absolute -right-8 top-1/2 -translate-y-1/2 z-10 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </button>
                            </>
                        )}

                        {/* Product cards */}
                        <div
                            ref={scrollContainerRef}
                            className="flex w-full max-w-full gap-4 overflow-x-auto pb-2 scrollbar-hide">
                            {allProducts.map((product) => (
                                <ProductCard key={product.key} product={product} />
                            ))}
                        </div>
                    </div>

                    {/* Pagination */}
                    <div
                        className="mt-6 flex items-center justify-center gap-2 text-mld-text-secondary"
                        role="navigation"
                        aria-label="Product pages">
                        {[1, 2, 3, 4, 5].map((page) => (
                            <button
                                type="button"
                                key={page}
                                aria-current={page === 1 ? 'page' : undefined}
                                className={` ${page === 1 ? 'text-mld-text-secondary' : 'text-mld-muted-text'}`}>
                                {page}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </SectionContainer>
    );
};

export default ProviderProducts;
